import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import _ from 'lodash';
import { InspectionStatus } from '@app/core/enums/inspection-status.enum';
import { Inspection } from '@app/core/models/inspection.model';
import { InspectionTypeGroup } from '@app/core/models/inspection-type-group.model';
import { HttpService } from '@app/core/http/http.service';
import { ProxiesService } from '@app/core/http/proxies.service';
import { HttpStatusCode } from '@app/core/enums/http-status-code.enum';
import { InspectionMessage } from '@app/modules/inspection/models/inspection-message.model';

@Injectable({ providedIn: 'root' })
export class InspectionService {
    private httpService = inject(HttpService);
    private proxiesService = inject(ProxiesService);

    private _currentInspection: Inspection;
    private _inspectionTypeGroup: InspectionTypeGroup;
    private uri = '';

    public get currentInspection() {
        return this._currentInspection;
    }

    constructor() {
        this.uri = environment.apiEndpoint + 'inspections';
    }

    public getInspections(categoryNames: string[]): Observable<Inspection[]> {
        const uri = this.uri + '/CSRWebsite' + '?categoryNames=' + categoryNames.join(',');
        return this.httpService.get(uri).pipe(
            map(res => (<any>res.body).data),
            catchError((error: any) => throwError(() => error)));
    }

    public getColumnMappingsInspections(): Observable<any> {
        const mappingUrl = environment.apiEndpoint + 'inspection-mappings';
        return this.httpService.get(mappingUrl + '/inspection-column-mappings').pipe(
            map(res => (<any>res.body).data),
            catchError((error: any) => throwError(() => error)));
    }

    public getInspection(id: string, includeDeleted = false): Observable<Inspection> {
        return this.httpService.get(this.uri + '/' + id + '?includeDeleted=' + includeDeleted).pipe(
            map(res => (<any>res.body).data),
            tap((inspection) => {
                this._currentInspection = Object.assign({}, inspection);
            }),
            catchError((error: any) => {
                this._currentInspection = null;
                return throwError(() => error);
            }));
    }

    public createInspection(information: any): Observable<string> {
        return this.httpService.post(this.uri, information).pipe(
            map(res => (<any>res.body).data),
            catchError((res: any) => throwError(() => res.error.data)));
    }

    public patchInspection(inspectionId: string, inspection: Inspection,
        isCallingFromOperationalSupportScreen?: boolean): Observable<any> {
        if (inspection.inspectionId == null) {
            inspection.inspectionId = inspectionId;
        }

        const payload = {
            ...inspection,
            isCallingFromOperationalSupportScreen: isCallingFromOperationalSupportScreen
        };

        return this.httpService.patch(this.uri + '/' + inspectionId, payload).pipe(
            map(() => {
                this._currentInspection = Object.assign({}, this._currentInspection, inspection);
            }),
            catchError((error: any) => throwError(() => error)));
    }

    public getInspectionInfo(): { key: string, value: string }[] {
        if (!this._currentInspection.information) {
            return null;
        }
        return Object.keys(this._currentInspection.information)
            .map(key => ({key: _.startCase(key), value: this._currentInspection.information[key]}));
    }

    public getInspectionMessage(inspectionId: string): Observable<InspectionMessage[]> {
        return this.httpService.get(this.uri + '/' + inspectionId + '/inspection-message/').pipe(
            map(res => (<any>res.body)?.data),
            catchError((error: any) => {
                this._currentInspection = null;
                return throwError(() => error);
            }));
    }

    /**
     * get inspection overview json schema
     * @member of InspectionService
     */
    public getInspectionTypeGroup(name): Observable<InspectionTypeGroup> {
        if (this._inspectionTypeGroup) {
            return of(this._inspectionTypeGroup);
        }

        const uri = environment.apiEndpoint + 'inspections-type-groups';
        return this.httpService.get(uri + '/' + name).pipe(
            map(res => (<any>res.body).data as InspectionTypeGroup),
            tap(i => {
                this._inspectionTypeGroup = Object.assign({}, i);
            }),
            catchError(error => throwError(() => error)));
    }

    public deleteInspection(inspectionId: string, comment: string) {
        const uri = `${environment.apiEndpoint}inspections/${inspectionId}?comment=${comment}`;
        return this.httpService.delete(uri).pipe(
            catchError(error => throwError(() => error)));
    }

    public getDisplayInspections(inspections: Inspection[]): Inspection[] {
        return _.filter(inspections, p => {
            return _.includes(this.inspectionStatusesForDisplay(), p.status);
        });
    }

    public inspectionStatusesForDisplay(): string[] {
        return [InspectionStatus.Creating,
            InspectionStatus.PreReadyToStart,
            InspectionStatus.ReadyToStart,
            InspectionStatus.Finalising,
            InspectionStatus.InProgress,
            InspectionStatus.Error];
    }

    public inspectionStatusesForSearch(): string[] {
        return [InspectionStatus.Passed, InspectionStatus.Failed];
    }

    public getCurrentInspection(): Inspection {
        return this._currentInspection;
    }

    public search(searchValue: string,
                  statuses: string[],
                  daysBack: number = 999,
                  includeDeleted: boolean = false,
                  categoryNames: string[] = []): Observable<Inspection[]> {
        const url = `${this.uri}/search`;
        const payload = {
            'queries': [
                {
                    'canonicalPath': 'BaseInformation',
                    'params': [
                        {
                            'key': 'vin',
                            'value': searchValue
                        },
                        {
                            'key': 'rego',
                            'value': searchValue
                        },
                        {
                            'key': 'chassis',
                            'value': searchValue
                        },
                        {
                            'key': 'vinOrRego',
                            'value': searchValue
                        }
                    ]
                },
                {
                    'canonicalPath': 'ExternalInspectionId',
                    'value': searchValue
                },

            ],
            'inspectionStatusArray': statuses,
            'daysBack': daysBack,
            'condition': 'OR',
            'includeDeleted': includeDeleted,
            'categoryNames': categoryNames
        };
        const response = this.httpService.post(url, payload);
        return response
            ? response.pipe(
                map(res => {
                    return (<any>res.body).data;
                }),
                catchError(() => {
                    return of([]);
                })
            )
            : of([]);
    }

    public searchLegacySystem(searchValue: string, statuses: string, endpoint: string, requestMethod: string)
        : Observable<Inspection[]> {
        const payload = {
            searchValue: searchValue,
            statuses: statuses
        };
        return this.proxiesService.getData(endpoint, requestMethod, payload).pipe(
            map(res => {
                return InspectionService.isResponseWithNoContent(res) ? [] : (<any>res.body).data;
            }),
            catchError(() => {
                return of([]);
            }));
    }

    public isRemovableInspection(inspection: Inspection) {
        return !(inspection.status === InspectionStatus.Finalising || inspection.deleted);
    }

    private static isResponseWithNoContent(res) {
        return res.status === HttpStatusCode.NoContent;
    }

    public verifyVehicleAge(inspectionId: string): Observable<any> {
        return this.httpService.get(this.uri + '/' + inspectionId + '/verify-vehicle-age')
            .pipe(
                map(res => (res.body as any).data),
                catchError((error: any) => {
                    return throwError(() => error);
                })
            );
    }
}